import { useEffect, useMemo, useRef, useState } from "react";
import { getBatchJobStatus, type JobStatus } from "@/api/workProcessor";
import type { Product, ProductBuildActivity, ProductBuildSummary } from "./types";

const FAILED_STATUSES = new Set(["failed", "error", "cancelled", "canceled"]);
const COMPLETE_STATUSES = new Set(["completed", "complete", "succeeded"]);

const clampProgress = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : null;

export function applyWorkerProgress(build: ProductBuildSummary, status: JobStatus): ProductBuildSummary {
    const raw = String(status.status || "pending").toLowerCase();
    const workerState = COMPLETE_STATUSES.has(raw)
        ? "completed"
        : FAILED_STATUSES.has(raw) || Boolean(status.error)
          ? "failed"
          : raw === "started" || raw === "running" || raw === "processing" || raw === "section_completed"
            ? "running"
            : "queued";
    const progress = workerState === "completed" ? 100 : clampProgress(status.progress);
    const message =
        status.description ||
        status.message ||
        (workerState === "queued"
            ? "Queued and preparing the base product context."
            : workerState === "running"
              ? "Building base product context and Work Products."
              : workerState === "completed"
                ? "Base product context is ready."
                : "Base product generation stopped before completion.");

    let foundBase = false;
    const activities: ProductBuildActivity[] = build.activities.map(activity => {
        if (activity.type !== "base_generation") return activity;
        foundBase = true;
        return {
            ...activity,
            status: workerState,
            progress,
            message,
            sections_completed: status.sections_completed,
            total_sections: status.total_sections
        };
    });
    if (!foundBase) {
        activities.push({
            id: "base_generation",
            type: "base_generation",
            label: "Building base product context",
            status: workerState,
            progress,
            message,
            external_job_id: build.processor_job_id,
            sections_completed: status.sections_completed,
            total_sections: status.total_sections
        });
    }

    // Context Mesh has its own lifecycle and retry control. It must not turn a
    // successfully generated Product back into a failed/processing core build
    // while this short-lived worker overlay is visible.
    const blockingActivities = activities.filter(activity => activity.type !== "context_mesh");
    const hasFailures = blockingActivities.some(activity => activity.status === "failed");
    const complete = blockingActivities.length > 0 && blockingActivities.every(activity => activity.status === "completed" || activity.status === "skipped");
    return {
        ...build,
        state: complete ? "completed" : hasFailures ? "failed" : "processing",
        has_failures: hasFailures,
        activities
    };
}

export function useProductBuildProgress(
    products: Product[],
    token: string | null | undefined,
    onTerminal: (productId: string) => void
): Record<string, ProductBuildSummary> {
    const [liveBuilds, setLiveBuilds] = useState<Record<string, ProductBuildSummary>>({});
    const onTerminalRef = useRef(onTerminal);
    onTerminalRef.current = onTerminal;

    const tracked = useMemo(
        () =>
            products.flatMap(product => {
                const live = liveBuilds[product.id];
                const persistedBuild = product.build;
                const build = live && live.processor_job_id === persistedBuild?.processor_job_id ? live : persistedBuild;
                const jobId = build?.processor_job_id;
                // A terminal worker response is only an overlay until ProductStudio
                // durably finishes feature import and records the whole Product build
                // completed. A transient failed snapshot must remain tracked: the
                // worker may still complete, and its terminal response is what drives
                // the canonical Product refresh that advances feature import.
                const durablyCompleted = persistedBuild?.state === "completed";
                return build && jobId && !durablyCompleted ? [{ productId: product.id, jobId, build }] : [];
            }),
        [products, liveBuilds]
    );
    const trackedKey = tracked
        .map(item => `${item.productId}:${item.jobId}`)
        .sort()
        .join("|");

    useEffect(() => {
        if (!token || tracked.length === 0) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const statuses = await getBatchJobStatus(
                    tracked.map(item => item.jobId),
                    token
                );
                if (cancelled) return;
                const terminalProducts = tracked.flatMap(item => {
                    const raw = String(statuses[item.jobId]?.status || "").toLowerCase();
                    return COMPLETE_STATUSES.has(raw) || FAILED_STATUSES.has(raw) ? [item.productId] : [];
                });
                setLiveBuilds(current => {
                    const next = { ...current };
                    for (const item of tracked) {
                        const status = statuses[item.jobId];
                        if (!status) continue;
                        const merged = applyWorkerProgress(next[item.productId] ?? item.build, status);
                        next[item.productId] = merged;
                    }
                    return next;
                });
                // Determine terminal transitions outside the React state
                // updater. Updaters may run later, after this function returns.
                // Repeating this callback is intentional until the refreshed
                // Product payload confirms durable completion or failure.
                terminalProducts.forEach(productId => onTerminalRef.current(productId));
            } catch {
                // Preserve the last trustworthy progress and retry on the next tick.
            }
        };
        void poll();
        const interval = window.setInterval(poll, 5000);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
        // trackedKey intentionally captures job identity without resetting on every progress update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, trackedKey]);

    return liveBuilds;
}
